/**
 * Hairo's floating blob, for pages without the sidebar card.
 *
 * CopilotProvider mounts it once, so it exists on every recruiter route. It
 * renders only when no sidebar "Ask Hairo" card is on screen (launcherSlot):
 * full-width pages such as a candidate's profile, and tablets and phones
 * where the rail is hidden behind the menu drawer.
 *
 * It starts at the bottom-left, bobs gently up and down, and can be held and
 * dragged anywhere on screen. A press that moves more than DRAG_PX is a drag
 * and does not open Hairo. The position is clamped to the viewport (also on
 * resize) and remembered in localStorage.
 *
 * What it opens on comes from the page itself (useCopilotPageSubject). Hidden
 * while the panel is open, and renders nothing unless useCopilotAvailability()
 * passes. In dev, the reason it is hidden (or forced) is logged once.
 */

import { Tooltip } from "antd";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useCopilot, useCopilotOpen } from "../CopilotProvider";
import { useSidebarCardVisible } from "../launcherSlot";
import { FORCE_KEY, useCopilotAvailability } from "../useCopilotAvailability";
import HairoOrb from "./HairoOrb";
import TaskBadge, { TaskRing } from "./TaskBadge";

export const POSITION_KEY = "hairo:fab-position";
/** Movement in px past which a press becomes a drag. */
const DRAG_PX = 5;
/** Minimum gap to the viewport edge, in px. */
const EDGE = 8;

function readPosition() {
  try {
    const raw = window.localStorage.getItem(POSITION_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return Number.isFinite(p?.x) && Number.isFinite(p?.y) ? { x: p.x, y: p.y } : null;
  } catch {
    return null;
  }
}

function savePosition(pos) {
  try {
    window.localStorage.setItem(POSITION_KEY, JSON.stringify(pos));
  } catch {
    // Storage blocked (private mode): the position is just not remembered.
  }
}

/** Keeps a top-left point so a box of `size` stays fully on screen. */
function clamp(pos, size) {
  const maxX = Math.max(EDGE, window.innerWidth - size - EDGE);
  const maxY = Math.max(EDGE, window.innerHeight - size - EDGE);
  return {
    x: Math.min(Math.max(pos.x, EDGE), maxX),
    y: Math.min(Math.max(pos.y, EDGE), maxY),
  };
}

export default function CopilotLauncher({ label = "Ask Hairo" }) {
  const copilot = useCopilot();
  const panelOpen = useCopilotOpen();
  const cardVisible = useSidebarCardVisible();
  const { available, forced, loading, reason } = useCopilotAvailability();

  // null = the CSS default corner; otherwise a dragged top-left point.
  const [pos, setPos] = useState(() => (typeof window === "undefined" ? null : readPosition()));
  const [dragging, setDragging] = useState(false);
  const boxRef = useRef(null);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);

  const loggedRef = useRef(false);
  useEffect(() => {
    if (!import.meta.env.DEV || loading || loggedRef.current) return;
    loggedRef.current = true;
    if (!available) {
      // eslint-disable-next-line no-console
      console.info("[copilot] entry point hidden:", reason);
    } else if (forced) {
      // eslint-disable-next-line no-console
      console.info(`[copilot] entry point FORCED via localStorage "${FORCE_KEY}", `, reason);
    }
  }, [available, forced, loading, reason]);

  const shown = Boolean(
    available && copilot && !panelOpen && !cardVisible && typeof document !== "undefined"
  );

  // A remembered point may be off screen on a smaller window: pull it back in.
  const reclamp = useCallback(() => {
    const size = boxRef.current?.offsetWidth || 56;
    setPos((p) => {
      if (!p) return p;
      const c = clamp(p, size);
      return c.x === p.x && c.y === p.y ? p : c;
    });
  }, []);

  useLayoutEffect(() => {
    if (!shown) return undefined;
    reclamp();
    window.addEventListener("resize", reclamp);
    return () => window.removeEventListener("resize", reclamp);
  }, [shown, reclamp]);

  if (!shown) return null;

  const onPointerDown = (e) => {
    if (e.button != null && e.button !== 0) return;
    const rect = boxRef.current?.getBoundingClientRect();
    if (!rect || !Number.isFinite(e.clientX) || !Number.isFinite(e.clientY)) return;
    dragRef.current = {
      id: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      offX: e.clientX - rect.left,
      offY: e.clientY - rect.top,
      size: rect.width || 56,
      moved: false,
    };
    suppressClickRef.current = false;
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      // Some test environments have no active pointer to capture.
    }
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;
    if (!Number.isFinite(e.clientX) || !Number.isFinite(e.clientY)) return;
    if (!d.moved) {
      if (Math.hypot(e.clientX - d.startX, e.clientY - d.startY) <= DRAG_PX) return;
      d.moved = true;
      setDragging(true);
    }
    setPos(clamp({ x: e.clientX - d.offX, y: e.clientY - d.offY }, d.size));
  };

  const endDrag = (e) => {
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {
      // Capture already released.
    }
    if (d.moved && Number.isFinite(e.clientX) && Number.isFinite(e.clientY)) {
      // The click that follows a drag must not open Hairo.
      suppressClickRef.current = true;
      setDragging(false);
      const final = clamp({ x: e.clientX - d.offX, y: e.clientY - d.offY }, d.size);
      setPos(final);
      savePosition(final);
    }
  };

  // Read at click time, so it opens on whatever page the user is on now.
  const open = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    copilot.openCopilot(copilot.getPageSubject());
  };

  const style = pos ? { left: `${pos.x}px`, top: `${pos.y}px`, bottom: "auto" } : undefined;

  return createPortal(
    <div
      ref={boxRef}
      className={`hairo-fab${dragging ? " hairo-fab--dragging" : ""}`}
      style={style}
    >
      <Tooltip title={dragging ? null : label} placement="right">
        <button
          type="button"
          className="hairo-fab__btn"
          onClick={open}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          aria-label={label}
        >
          {/* Sized from CSS: 56px, 44px on phones. */}
          <HairoOrb size={null} className="hairo-fab__orb" />
          {/* A task running with the panel closed: how far it has got. */}
          <TaskRing size={64} />
        </button>
      </Tooltip>
      <TaskBadge className="hairo-fab__badge" />
    </div>,
    document.body
  );
}
